import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BellRing,
  CalendarClock,
  CheckCircle2,
  Pencil,
  Plus,
  Trash2,
} from 'lucide-react';
import { useState, type FormEvent } from 'react';

import { useAuth } from '@/contexts/AuthContext';
import { ApiError, apiFetch } from '@/lib/api';

type Recorrencia = 'NONE' | 'MONTHLY' | 'QUARTERLY' | 'SEMIANNUAL' | 'YEARLY';

export interface PaymentReminder {
  id: string;
  descricao: string;
  fornecedor: string | null;
  valor: string | null;
  proximoVencimento: string;
  recorrencia: Recorrencia;
  diasAntes: number;
  categoryId: string | null;
  lancarNoFinanceiro: boolean;
  bankAccountId: string | null;
  observacao: string | null;
  ultimoPagamento: string | null;
  isActive: boolean;
  diasRestantes: number;
  vencido: boolean;
  emAlerta: boolean;
}

interface Category {
  id: string;
  codigo: string;
  nome: string;
  kind: string;
}

interface BankAccount {
  id: string;
  name: string;
}

const RECORRENCIA_LABEL: Record<Recorrencia, string> = {
  NONE: 'Uma vez só',
  MONTHLY: 'Todo mês',
  QUARTERLY: 'A cada 3 meses',
  SEMIANNUAL: 'A cada 6 meses',
  YEARLY: 'Todo ano',
};

function formatarData(iso: string): string {
  return new Date(iso).toLocaleDateString('pt-BR', { timeZone: 'UTC' });
}

function formatarValor(valor: string | null): string {
  if (valor === null) return '—';
  return Number(valor).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

/** "vence em 3 dias", "vence hoje", "13 dias atrasado" — o dado que decide a ação. */
function textoPrazo(r: PaymentReminder): string {
  if (r.diasRestantes === 0) return 'vence hoje';
  if (r.diasRestantes < 0) {
    const dias = Math.abs(r.diasRestantes);
    return `${dias} ${dias === 1 ? 'dia' : 'dias'} atrasado`;
  }
  return `vence em ${r.diasRestantes} ${r.diasRestantes === 1 ? 'dia' : 'dias'}`;
}

export function RemindersPage() {
  const { user } = useAuth();
  const canManage = user?.role === 'SUPER_ADMIN' || user?.role === 'ADMIN';
  const qc = useQueryClient();
  const [editando, setEditando] = useState<PaymentReminder | null>(null);
  const [criando, setCriando] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['reminders'],
    queryFn: () => apiFetch<PaymentReminder[]>('/reminders'),
  });

  const pagar = useMutation({
    mutationFn: (id: string) =>
      apiFetch<PaymentReminder>(`/reminders/${id}/pagar`, {
        method: 'POST',
        body: JSON.stringify({}),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['reminders'] });
      void qc.invalidateQueries({ queryKey: ['financial'] });
    },
  });

  const remover = useMutation({
    mutationFn: (id: string) => apiFetch<void>(`/reminders/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['reminders'] }),
  });

  const ativos = (data ?? []).filter((r) => r.isActive);
  const emAlerta = ativos.filter((r) => r.emAlerta);
  const tranquilos = ativos.filter((r) => !r.emAlerta);
  const encerrados = (data ?? []).filter((r) => !r.isActive);

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Lembretes de pagamento</h1>
          <p className="text-sm text-muted-foreground">
            Contas que a casa paga e não podem ser esquecidas — VPS, domínio, certificado,
            licenças. Ao marcar como paga, a data avança sozinha para o próximo ciclo.
          </p>
        </div>
        {canManage && (
          <button
            type="button"
            onClick={() => setCriando(true)}
            className="flex items-center gap-1 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            <Plus className="h-4 w-4" /> Novo lembrete
          </button>
        )}
      </header>

      {isLoading && <p className="text-sm text-muted-foreground">Carregando…</p>}
      {isError && <p className="text-sm text-destructive">Erro ao carregar.</p>}

      {data && data.length === 0 && (
        <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed border-border bg-card p-12 text-center">
          <CalendarClock className="h-10 w-10 text-muted-foreground/50" />
          <p className="text-sm text-muted-foreground">
            Nenhum lembrete cadastrado. Comece pelas contas que doem se atrasarem:
            hospedagem, domínio, certificado digital.
          </p>
        </div>
      )}

      {emAlerta.length > 0 && (
        <section className="space-y-2">
          <h2 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
            <BellRing className="h-4 w-4" /> Pedem atenção
          </h2>
          <div className="space-y-2">
            {emAlerta.map((r) => (
              <Linha
                key={r.id}
                r={r}
                canManage={canManage}
                onEditar={() => setEditando(r)}
                onPagar={() => pagar.mutate(r.id)}
                onRemover={() => {
                  if (confirm(`Excluir o lembrete "${r.descricao}"?`)) remover.mutate(r.id);
                }}
                pagando={pagar.isPending && pagar.variables === r.id}
              />
            ))}
          </div>
        </section>
      )}

      {tranquilos.length > 0 && (
        <section className="space-y-2">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
            Próximas
          </h2>
          <div className="space-y-2">
            {tranquilos.map((r) => (
              <Linha
                key={r.id}
                r={r}
                canManage={canManage}
                onEditar={() => setEditando(r)}
                onPagar={() => pagar.mutate(r.id)}
                onRemover={() => {
                  if (confirm(`Excluir o lembrete "${r.descricao}"?`)) remover.mutate(r.id);
                }}
                pagando={pagar.isPending && pagar.variables === r.id}
              />
            ))}
          </div>
        </section>
      )}

      {encerrados.length > 0 && (
        <section className="space-y-2">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
            Encerrados
          </h2>
          <div className="space-y-2 opacity-60">
            {encerrados.map((r) => (
              <Linha
                key={r.id}
                r={r}
                canManage={canManage}
                onEditar={() => setEditando(r)}
                onPagar={() => pagar.mutate(r.id)}
                onRemover={() => {
                  if (confirm(`Excluir o lembrete "${r.descricao}"?`)) remover.mutate(r.id);
                }}
                pagando={false}
              />
            ))}
          </div>
        </section>
      )}

      {(criando || editando) && (
        <ReminderDialog
          inicial={editando}
          onClose={() => {
            setCriando(false);
            setEditando(null);
          }}
          onSaved={() => {
            setCriando(false);
            setEditando(null);
            void qc.invalidateQueries({ queryKey: ['reminders'] });
          }}
        />
      )}
    </div>
  );
}

function Linha({
  r,
  canManage,
  onEditar,
  onPagar,
  onRemover,
  pagando,
}: {
  r: PaymentReminder;
  canManage: boolean;
  onEditar: () => void;
  onPagar: () => void;
  onRemover: () => void;
  pagando: boolean;
}) {
  const cor = r.vencido
    ? 'border-destructive/40 bg-destructive/5'
    : r.emAlerta
      ? 'border-amber-500/40 bg-amber-500/5'
      : 'border-border bg-card';

  return (
    <article className={`flex flex-wrap items-center gap-3 rounded-lg border p-3 ${cor}`}>
      <div className="min-w-0 flex-1">
        <p className="truncate font-medium">
          {r.descricao}
          {r.fornecedor && (
            <span className="ml-2 text-xs font-normal text-muted-foreground">
              {r.fornecedor}
            </span>
          )}
        </p>
        <p className="text-xs text-muted-foreground">
          {formatarData(r.proximoVencimento)}
          {r.isActive && (
            <span
              className={
                r.vencido
                  ? 'ml-2 font-medium text-destructive'
                  : r.emAlerta
                    ? 'ml-2 font-medium text-amber-700'
                    : 'ml-2'
              }
            >
              · {textoPrazo(r)}
            </span>
          )}
          <span className="ml-2">· {RECORRENCIA_LABEL[r.recorrencia]}</span>
          {r.lancarNoFinanceiro && <span className="ml-2">· lança no financeiro</span>}
        </p>
      </div>

      <span className="font-mono text-sm tabular-nums">{formatarValor(r.valor)}</span>

      {canManage && (
        <div className="flex items-center gap-1">
          {r.isActive && (
            <button
              type="button"
              disabled={pagando}
              onClick={onPagar}
              data-dica="Registra o pagamento e avança para o próximo vencimento"
              className="flex items-center gap-1 rounded-md border border-input px-2 py-1 text-xs hover:bg-accent disabled:opacity-50"
            >
              <CheckCircle2 className="h-3 w-3" />
              {pagando ? 'Registrando…' : 'Paguei'}
            </button>
          )}
          <button
            type="button"
            onClick={onEditar}
            data-dica="Editar"
            className="rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <Pencil className="h-3 w-3" />
          </button>
          <button
            type="button"
            onClick={onRemover}
            data-dica="Excluir"
            className="rounded-md p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
          >
            <Trash2 className="h-3 w-3" />
          </button>
        </div>
      )}
    </article>
  );
}

function ReminderDialog({
  inicial,
  onClose,
  onSaved,
}: {
  inicial: PaymentReminder | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState({
    descricao: inicial?.descricao ?? '',
    fornecedor: inicial?.fornecedor ?? '',
    valor: inicial?.valor ?? '',
    proximoVencimento: inicial?.proximoVencimento.slice(0, 10) ?? '',
    recorrencia: inicial?.recorrencia ?? ('MONTHLY' as Recorrencia),
    diasAntes: String(inicial?.diasAntes ?? 7),
    categoryId: inicial?.categoryId ?? '',
    lancarNoFinanceiro: inicial?.lancarNoFinanceiro ?? false,
    bankAccountId: inicial?.bankAccountId ?? '',
    observacao: inicial?.observacao ?? '',
    isActive: inicial?.isActive ?? true,
  });
  const [erro, setErro] = useState<string | null>(null);

  const categorias = useQuery({
    queryKey: ['categories', 'EXPENSE'],
    queryFn: () => apiFetch<Category[]>('/categories?kind=EXPENSE&isActive=true'),
  });
  const contas = useQuery({
    queryKey: ['bank-accounts'],
    queryFn: () => apiFetch<BankAccount[]>('/bank-accounts'),
  });

  const salvar = useMutation({
    mutationFn: () => {
      const body = {
        descricao: form.descricao,
        fornecedor: form.fornecedor || undefined,
        valor: form.valor === '' ? undefined : Number(form.valor),
        proximoVencimento: form.proximoVencimento,
        recorrencia: form.recorrencia,
        diasAntes: Number(form.diasAntes),
        categoryId: form.categoryId || undefined,
        lancarNoFinanceiro: form.lancarNoFinanceiro,
        bankAccountId: form.bankAccountId || undefined,
        observacao: form.observacao || undefined,
        isActive: form.isActive,
      };
      return inicial
        ? apiFetch<PaymentReminder>(`/reminders/${inicial.id}`, {
            method: 'PATCH',
            body: JSON.stringify(body),
          })
        : apiFetch<PaymentReminder>('/reminders', {
            method: 'POST',
            body: JSON.stringify(body),
          });
    },
    onSuccess: onSaved,
    onError: (e) =>
      setErro(e instanceof ApiError ? e.message : 'Não foi possível salvar o lembrete.'),
  });

  const enviar = (e: FormEvent) => {
    e.preventDefault();
    setErro(null);
    salvar.mutate();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <form
        onSubmit={enviar}
        className="max-h-[90vh] w-full max-w-2xl space-y-4 overflow-y-auto rounded-lg bg-card p-6 shadow-xl"
      >
        <h2 className="text-lg font-semibold">
          {inicial ? 'Editar lembrete' : 'Novo lembrete de pagamento'}
        </h2>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <Campo label="O que é *">
            <input
              required
              className="input"
              placeholder="VPS Hostinger"
              value={form.descricao}
              onChange={(e) => setForm((s) => ({ ...s, descricao: e.target.value }))}
            />
          </Campo>

          <Campo label="Para quem se paga">
            <input
              className="input"
              placeholder="Hostinger"
              value={form.fornecedor}
              onChange={(e) => setForm((s) => ({ ...s, fornecedor: e.target.value }))}
            />
          </Campo>

          <Campo label="Próximo vencimento *">
            <input
              required
              type="date"
              className="input"
              value={form.proximoVencimento}
              onChange={(e) => setForm((s) => ({ ...s, proximoVencimento: e.target.value }))}
            />
          </Campo>

          <Campo label="Repete">
            <select
              className="input"
              value={form.recorrencia}
              onChange={(e) =>
                setForm((s) => ({ ...s, recorrencia: e.target.value as Recorrencia }))
              }
            >
              {(Object.keys(RECORRENCIA_LABEL) as Recorrencia[]).map((k) => (
                <option key={k} value={k}>
                  {RECORRENCIA_LABEL[k]}
                </option>
              ))}
            </select>
          </Campo>

          <Campo label="Valor">
            <input
              type="number"
              step="0.01"
              min="0"
              className="input font-mono"
              placeholder="deixe vazio se varia"
              value={form.valor}
              onChange={(e) => setForm((s) => ({ ...s, valor: e.target.value }))}
            />
          </Campo>

          <Campo label="Avisar quantos dias antes">
            <input
              type="number"
              min="0"
              max="180"
              className="input"
              value={form.diasAntes}
              onChange={(e) => setForm((s) => ({ ...s, diasAntes: e.target.value }))}
            />
            <span className="text-[10px] text-muted-foreground">
              A partir daí o lembrete sobe para o topo da lista e aparece no Dashboard.
            </span>
          </Campo>
        </div>

        <fieldset className="space-y-3 rounded-md border border-border p-3">
          <legend className="px-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Financeiro (opcional)
          </legend>

          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={form.lancarNoFinanceiro}
              onChange={(e) =>
                setForm((s) => ({ ...s, lancarNoFinanceiro: e.target.checked }))
              }
            />
            <span>
              Lançar como despesa ao marcar &quot;Paguei&quot;
              <span className="block text-xs text-muted-foreground">
                Deixe desmarcado se esta conta não deve entrar no fluxo de caixa —
                serve só como lembrete.
              </span>
            </span>
          </label>

          {form.lancarNoFinanceiro && (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <Campo label="Categoria de despesa">
                <select
                  className="input"
                  value={form.categoryId}
                  onChange={(e) => setForm((s) => ({ ...s, categoryId: e.target.value }))}
                >
                  <option value="">— sem categoria —</option>
                  {(categorias.data ?? []).map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.codigo} · {c.nome}
                    </option>
                  ))}
                </select>
              </Campo>

              <Campo label="Conta de onde sai">
                <select
                  className="input"
                  value={form.bankAccountId}
                  onChange={(e) => setForm((s) => ({ ...s, bankAccountId: e.target.value }))}
                >
                  <option value="">— não informar —</option>
                  {(contas.data ?? []).map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </Campo>
            </div>
          )}
        </fieldset>

        <Campo label="Observação">
          <textarea
            className="input min-h-[60px]"
            placeholder="número do contrato, onde pagar, com quem falar…"
            value={form.observacao}
            onChange={(e) => setForm((s) => ({ ...s, observacao: e.target.value }))}
          />
        </Campo>

        {inicial && (
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(e) => setForm((s) => ({ ...s, isActive: e.target.checked }))}
            />
            Lembrete ativo
          </label>
        )}

        {erro && <p className="text-sm text-destructive">{erro}</p>}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-input px-3 py-2 text-sm hover:bg-accent"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={salvar.isPending}
            className="rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {salvar.isPending ? 'Salvando…' : 'Salvar'}
          </button>
        </div>
      </form>
    </div>
  );
}

function Campo({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}
